import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import pb from '../../lib/pocketbase';
import { Package, X, Save } from 'lucide-react';
import styles from './ProjectForm.module.css';

function InventoryForm({ item, onSave, onCancel }) {
    const [formData, setFormData] = useState({
        name: '',
        type: 'MATERIEL', // Default
        quantity: 0,
        min_quantity: 0,
        assigned_to: '',
        location: '',
        category: 'AUTRE',
        image: null,
        maintenance_interval: 0
    });

    const [users, setUsers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        loadUsers();
        if (item) {
            setFormData({
                name: item.name || '',
                type: item.type || 'MATERIEL',
                quantity: item.quantity || 0,
                min_quantity: item.min_quantity || 0,
                assigned_to: item.assigned_to || '',
                location: item.location || '',
                category: item.category || 'AUTRE',
                image: item.image || null,
                maintenance_interval: item.maintenance_interval || 0
            });
        }
    }, [item]);

    const loadUsers = async () => {
        try {
            // Load users to assign tools to (employees + admins)
            const records = await pb.collection('users').getFullList({
                sort: 'username',
                filter: 'role = "admin" || role = "employee"'
            });
            setUsers(records);
        } catch (err) {
            // toast.error("Erreur chargement utilisateurs");
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'image') {
            setFormData(prev => ({ ...prev, image: files[0] }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = "Le nom est requis";
        if (formData.quantity < 0) errors.quantity = "La quantité ne peut pas être négative";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            let record;
            const payload = { ...formData };

            // Convert numbers
            payload.quantity = Number(payload.quantity);
            payload.min_quantity = Number(payload.min_quantity);
            payload.maintenance_interval = Number(payload.maintenance_interval);
            if (payload.assigned_to === '') payload.assigned_to = null;

            // Handle file upload
            const data = new FormData();
            Object.keys(payload).forEach(key => {
                if (payload[key] !== null && payload[key] !== undefined) {
                    data.append(key, payload[key]);
                }
            });

            if (item) {
                record = await pb.collection('inventory').update(item.id, data);
                toast.success("Article mis à jour");
            } else {
                record = await pb.collection('inventory').create(data);
                toast.success("Article ajouté au stock");
            }
            onSave(record);
        } catch (err) {
            console.error("Erreur sauvegarde inventaire", err);
            toast.error("Erreur : " + (err.message || "Impossible de sauvegarder"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
            <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 700, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    
                    {/* Header */}
                    <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                                <Package size={20} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>{item ? 'Modifier Article' : 'Nouvel Article'}</h3>
                                <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>Gestion de l'inventaire</p>
                            </div>
                        </div>
                        <button type="button" onClick={onCancel} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div style={{ padding: '25px 30px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>

                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
                            <div className={styles.formGroup}>
                                <label htmlFor="name" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Nom de l'article *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Ex: Perceuse Bosch ou Sac de Ciment"
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                                />
                                {formErrors.name && <div className={styles.fieldError}>{formErrors.name}</div>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="type" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Type</label>
                                <select
                                    id="type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                                >
                                    <option value="MATERIEL">Matériel</option>
                                    <option value="OUTIL">Outil</option>
                                </select>
                            </div>

                            {formData.type === 'OUTIL' && (
                                <div className={styles.formGroup}>
                                    <label htmlFor="maintenance_interval" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Maint. (Jrs)</label>
                                    <input
                                        type="number"
                                        id="maintenance_interval"
                                        name="maintenance_interval"
                                        value={formData.maintenance_interval}
                                        onChange={handleChange}
                                        placeholder="Ex: 180"
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className={styles.formGroup}>
                                <label htmlFor="quantity" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Quantité Actuelle</label>
                                <input
                                    type="number"
                                    id="quantity"
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="min_quantity" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Alerte Stock Bas (Min)</label>
                                <input
                                    type="number"
                                    id="min_quantity"
                                    name="min_quantity"
                                    value={formData.min_quantity}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="location" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Emplacement de stockage</label>
                            <input
                                type="text"
                                id="location"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder="Ex: Entrepôt A, Étagère 3"
                                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="category" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Catégorie</label>
                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                            >
                                <option value="ELECTRICITE">⚡ Électricité</option>
                                <option value="PLOMBERIE">🚰 Plomberie</option>
                                <option value="MACONNERIE">🧱 Maçonnerie</option>
                                <option value="PEINTURE">🎨 Peinture</option>
                                <option value="SECURITE">🦺 Sécurité</option>
                                <option value="OUTILLAGE">⚙️ Outillage</option>
                                <option value="CONSOMMABLES">📦 Consommables</option>
                                <option value="AUTRE">📁 Autre</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="image" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Photo de l'article</label>
                            <input
                                type="file"
                                id="image"
                                name="image"
                                accept="image/*"
                                onChange={handleChange}
                                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                            />
                            {formData.image && (
                                <div style={{ marginTop: '10px' }}>
                                    <img
                                        src={typeof formData.image === 'string' ? pb.files.getURL(item, formData.image) : URL.createObjectURL(formData.image)}
                                        alt="Preview"
                                        style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '12px' }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="assigned_to" style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Assigné à (Optionnel)</label>
                            <select
                                id="assigned_to"
                                name="assigned_to"
                                value={formData.assigned_to}
                                onChange={handleChange}
                                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}
                            >
                                <option value="">-- Personne --</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.username}</option>
                                ))}
                            </select>
                            <small style={{ display: 'block', marginTop: '6px', fontSize: '0.8rem', color: 'var(--color-text-light)' }}>Utile pour savoir qui a emprunté un outil.</small>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div />
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button type="button" onClick={onCancel} disabled={isSubmitting} style={{ padding: '10px 24px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                                Annuler
                            </button>
                            <button type="submit" disabled={isSubmitting} style={{ padding: '10px 28px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)' }}>
                                <Save size={18} /> {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}

export default InventoryForm;
